import { Stack } from "@ui/Stack";
import { navigateToAccountsPath } from "@ui/layouts/AccountLayout/accountsReferrer";
import { createNotifications, NotificationsButton } from "./NotificationsButton";
import { ProfileButton } from "./ProfileButton";
import "./UserPanel.css";
import { Text } from "@ui/typography/Text";
import { createResource } from "solid-js";
import { getKnockFeedAuth } from "@/client";
import type { KnockFeedAuth } from "./createKnockFeed";

type User = {
	// url to user's avatar image
	userAvatar: string;
	// user's name
	userName: string;
	// user's email
	userEmail: string;
	// user's profile handle with @ prefix
	influencerHandle: string;
};

type Props = {
	user: User;
	referrer: "studio" | "askgvt";
};

async function fetchKnockAuth(): Promise<KnockFeedAuth> {
	const response = await getKnockFeedAuth();
	const data = response.data;
	if (response.error || !data?.user_id) {
		throw new Error("Failed to load Knock feed config");
	}

	return {
		publicKey: data.public_key ?? null,
		feedChannelId: data.feed_channel_id ?? null,
		userId: data.user_id,
		userToken: data.user_token ?? undefined,
	};
}

export const UserPanel = (props: Props) => {
	const handle = props.user.influencerHandle
		? `@${props.user.influencerHandle}`
		: null;

	const [auth] = createResource(fetchKnockAuth);
	const notifications = createNotifications(auth);
	return (
		<>
			<Stack
				class="details"
				direction="row"
				spacing="medium"
				align="center"
				fullWidth
				justify="space-between"
			>
				<Stack direction="row" spacing="medium" align="center">
					<Avatar user={props.user} referrer={props.referrer} />
					<Stack class="user-identification" direction="column" spacing="none">
						<Text as="span" weight="bold" color="ink" ellipsis>
							{props.user.userName}
						</Text>
						<Text as="span" size="small" color="fg3" ellipsis>
							{handle ?? props.user.userEmail}
						</Text>
					</Stack>
				</Stack>
				<Stack
					class="gvt-account__actions"
					direction="row"
					spacing="small"
					justify="end"
				>
					<NotificationsButton notifications={notifications} />
					<ProfileButton referrer={props.referrer} />
				</Stack>
			</Stack>
			<div class="details-brief">
				<Avatar user={props.user} referrer={props.referrer} />
				<ProfileButton referrer={props.referrer} />
				<NotificationsButton notifications={notifications} />
			</div>
		</>
	);
};

const Avatar = (props: { user: User; referrer: "studio" | "askgvt" }) => {
	return (
		<button
			type="button"
			class="user-avatar"
			aria-label="Account settings"
			onClick={() => navigateToAccountsPath("/account", props.referrer)}
		>
			{props.user.userAvatar ? (
				<img src={props.user.userAvatar} alt="" />
			) : (
				<span class="label">{props.user.userName?.[0] ?? ""}</span>
			)}
		</button>
	);
};
